import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'

import Drawer from 'components/Drawer/Drawer'

import styles from './Ticket.module.sass'
import InfoList from 'components/InfoList/InfoList'
import VStack from '@jimmyboys/liita-ui/dist/components/VStack'
import {
	getStateValueByTicketState,
	getTitleByTicketType,
} from 'components/cards/TicketCard/TicketCard'
import { useCallback, useMemo } from 'react'
import { getDrawerTypeFromTicketType } from 'utils'
import { useDrawerContext } from 'context/Drawer/DrawerContext'
import HStack from '@jimmyboys/liita-ui/dist/components/HStack'
import Icon from '@jimmyboys/liita-ui/dist/components/Icon'
import ModalOptions from '@jimmyboys/liita-ui/dist/components/ModalOptions'
import { useReload } from 'store'

const DrawerHeader = (props) => {
	const { data, handleClose } = props
	const { i18n, showDialog } = useGlobal()
	const reload = useReload()
	const { id } = data

	const options = useMemo(() => {
		if ([5, 6].includes(data?.ticketStatus?.id)) {
			return null
		}

		return [
			{
				value: 'cancel',
				text: i18n`Nullify ticket`,
				action: () => {
					showDialog(
						'TicketNullifyRequestDialog',
						i18n`Nullify ticket`,
						{
							processId: data.processId,
							ticketId: data.id,
							childs: data.childs,
						},
						false,
						undefined,
						'center',
						'nullify-ticket-dialog'
					)
						.then(() => {
							reload([
								`/clinical_processes/${data.processId}/tickets`,
								`/clinical_processes/${data.processId}/tasks`,
								`/metadata/tickets/${data.id}`,
							])
							handleClose()
						})
						.catch(() => null)
				},
			},
		]
	}, [data, i18n, showDialog, reload, handleClose])

	return (
		<HStack className={styles['drawer-header']}>
			<h5>#{id}</h5>
			{options && options.length && (
				<ModalOptions
					className={styles['actions-modal']}
					options={options}
					onSelect={(item) => {
						item && item.action && item.action()
					}}
				>
					<button className={styles['drawer-options']}>
						<Icon width='32px' height='32px' name='more' />
					</button>
				</ModalOptions>
			)}
		</HStack>
	)
}

const TicketHeader = (props) => {
	const { i18n } = useGlobal()
	const ticketStatus = props?.status
	const date = props?.date
	const type = props?.type
	const appointment = props?.appointment
	const requisition = props?.requisition
	const parent = props?.parent

	const ticketStatusValue = getStateValueByTicketState(ticketStatus?.id)
	const ticketTypeValue = getTitleByTicketType(type)

	return (
		<VStack
			justify='space-between'
			gap='xsmall'
			className={styles['header']}
		>
			<h2>{i18n.translate(ticketTypeValue)}</h2>
			<h5
				className={`${styles['ticket-status']} ${
					styles[`status-${ticketStatusValue}`]
				}`}
			>
				{ticketStatus?.description}
			</h5>
			{appointment && (
				<small>
					{i18n`Associated to appointment`} #{appointment.id}
				</small>
			)}
			{requisition && (
				<small>
					{i18n`Associated to requisition`} #{requisition.id}
				</small>
			)}
			{parent && (
				<small>
					{i18n`Associated to ticket`} #{parent.id}
				</small>
			)}
			<small>{i18n`Openned at ${date?.shortDateFormat}`}</small>
		</VStack>
	)
}

const Ticket = (props) => {
	const { dispatch } = useDrawerContext()
	const { handleClose, data } = props
	const {
		author,
		description,
		creationDate,
		ticketStatus,
		ticketType,
		appointment,
		requisition,
		parentTicket,
		voidReason,
		answer,
		responseUser,
		updateUser,
		responseDate,
		childs,
	} = data

	console.log(data)

	const { i18n } = useGlobal()

	const items = [
		{
			title: i18n`Openned by`,
			text: author,
			icon: 'profile',
		},
		{
			title: i18n`Description`,
			text: description,
			icon: 'info',
		},
	]

	if (voidReason) {
		items.push({
			title: i18n`Nullify reason`,
			text: voidReason,
			icon: 'info',
		})
	}

	const answerItems = [
		{
			title: i18n`Answer by`,
			text: responseUser ?? updateUser,
			icon: 'profile',
		},
		{
			title: i18n`Answer`,
			text: answer,
			icon: 'info',
		},
		{
			title: i18n`Date`,
			text: responseDate?.shortDateFormat,
			icon: 'calendar',
		},
	]

	const openChild = useCallback(
		(value) => (event) => {
			event.preventDefault()

			console.log(value)

			dispatch({
				action: 'OPEN_DRAWER',
				payload: {
					type: getDrawerTypeFromTicketType(value.ticketType),
					data: {
						...value,
					},
				},
			})
		},
		[dispatch]
	)

	return (
		<Drawer
			dialogId={'drawer'}
			handleClose={handleClose}
			customHeader={
				<DrawerHeader data={data} handleClose={handleClose} />
			}
		>
			<div className={styles['root']}>
				<TicketHeader
					date={creationDate}
					status={ticketStatus}
					type={ticketType}
					appointment={appointment}
					requisition={requisition}
					parent={parentTicket}
				/>
				<div className={styles['content']}>
					<section className={styles['info-section']}>
						<div>
							<h4>{i18n`Requested`}</h4>
							<InfoList items={items} />
						</div>
						{answer && (
							<div>
								<h4>{i18n`Answer`}</h4>
								<InfoList items={answerItems} />
							</div>
						)}
						{childs?.length ? (
							<div>
								<h4>{i18n`Associated tickets`}</h4>
								{childs.map((child, index) => {
									const ticketStatusValue =
										getStateValueByTicketState(
											child.ticketStatus?.id
										)

									return (
										<HStack
											className={
												styles['child-ticket-root']
											}
											onClick={openChild(child)}
											key={index}
										>
											<div
												className={`
													${styles['child-status']}
													${styles[`status-${ticketStatusValue}`]}
												`}
											/>
											<Icon
												width='20px'
												height='20px'
												name='ticket'
											/>
											<VStack
												className={
													styles['child-content']
												}
											>
												<h5 key={index}>
													#{child.id} -{' '}
													{i18n.translate(
														getTitleByTicketType(
															child.ticketType
														)
													)}
												</h5>
												<small>
													{child.description}
												</small>
											</VStack>
										</HStack>
									)
								})}
							</div>
						) : null}
					</section>
				</div>
			</div>
		</Drawer>
	)
}

export default Ticket
